/**
 * Percentage calculation helpers for LadderPercentField
 */

/**
 * Calculate percentage difference from baseline
 * @param {number} currentValue - The current value
 * @param {number} baseline - The baseline value (observationValueEnd)
 * @returns {number} - Percentage difference
 */
export const calculatePercentageDiff = (currentValue, baseline) => {
  if (baseline === 0) return 0
  return ((currentValue - baseline) / baseline) * 100
}

/**
 * Convert absolute min/max values to percentage values relative to baseline
 * @param {number} absoluteValue - The absolute min or max value
 * @param {number} baseline - The baseline value
 * @param {number} fallback - Fallback value if baseline is 0
 * @returns {number} - Percentage value
 */
export const convertToPercentageLimit = (absoluteValue, baseline, fallback) => {
  if (baseline === 0) return fallback
  return ((absoluteValue - baseline) / baseline) * 100
}

/**
 * Convert percentage value back to absolute value using baseline
 * @param {number} percentValue - The percentage value
 * @param {number} baseline - The baseline value
 * @returns {number} - Absolute value
 */
export const convertToAbsoluteValue = (percentValue, baseline) => {
  return baseline * (1 + percentValue / 100)
}

/**
 * Smart snapping logic for percentage values
 * Snaps to nearest whole number based on direction of change
 * @param {number} currentDisplayValue - Current percentage display value
 * @param {number} newPercentValue - New percentage value from input
 * @returns {number} - Target percentage value after snapping
 */
export const applySmartSnapping = (currentDisplayValue, newPercentValue) => {
  const currentIsWholeNumber =
    Math.abs(currentDisplayValue - Math.round(currentDisplayValue)) < 0.01

  if (!currentIsWholeNumber) {
    // If current value is between whole numbers, snap based on direction
    const roundedCurrent = Math.round(currentDisplayValue)
    if (newPercentValue > currentDisplayValue) {
      // Increasing - snap to ceiling or closest whole number
      return newPercentValue > roundedCurrent
        ? roundedCurrent
        : Math.ceil(currentDisplayValue)
    } else {
      // Decreasing - snap to floor or closest whole number
      return newPercentValue < roundedCurrent
        ? roundedCurrent
        : Math.floor(currentDisplayValue)
    }
  } else {
    // Current value is already a whole number, proceed with normal increment/decrement
    return newPercentValue
  }
}
